import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Drawer,
  IconButton,
  Link,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, EditOutlined as EditIcon, WarningAmberRounded as WarningIcon } from '@mui/icons-material';
import { userInitials } from '../UserMenu';
import { PanelSection, sidePanelBackdropSx, sidePanelPaperSx } from '../SidePanel';
import { getRoleDisplayName } from '../../utils/roleManager';
import { createUser, descendantIds, fetchUserAssignments, updateUser, validateUserForm } from '../../utils/usersService';
import { GREEN, GREEN_SOFT, INK, LINE, MUTED } from '../home/homeTheme';

// Colori per ruolo applicativo (id ROLE_IDS): sfondo chiaro + testo scuro della stessa tinta
const ROLE_COLORS = {
  1: ['#FCEBEB', '#791F1F'],
  2: ['#F1EFE8', '#444441'],
  3: ['#E6F1FB', '#0C447C'],
  4: ['#E1F5EE', '#085041'],
  5: ['#EEEDFE', '#3C3489'],
  6: ['#FAEEDA', '#633806'],
  7: ['#FBEAF0', '#72243E'],
};

export function RoleChip({ roleId }) {
  const [bg, fg] = ROLE_COLORS[roleId] || ROLE_COLORS[2];
  return (
    <Box component="span" sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 999, bgcolor: bg, color: fg, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {roleId ? getRoleDisplayName(Number(roleId)) : 'No role'}
    </Box>
  );
}

export function UserAvatar({ name, size = 32 }) {
  return (
    <Avatar sx={{ width: size, height: size, fontSize: size * 0.38, fontWeight: 700, bgcolor: GREEN_SOFT, color: 'var(--rt-green-dark)' }}>{userInitials({ name })}</Avatar>
  );
}

const EMPTY_FORM = { username: '', email: '', role_id: '', org_role_id: '', id_people_manager: '' };
const formFrom = (u) => ({
  username: u.username || '',
  email: u.email || '',
  role_id: u.role_id || '',
  org_role_id: u.org_role_id || '',
  id_people_manager: u.id_people_manager || '',
});
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const REPORTS_PREVIEW = 12;
function PersonLink({ person, onOpen }) {
  return (
    <ButtonBase onClick={() => onOpen(person.id)} sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-start', textAlign: 'left', borderRadius: 2, p: 0.5, mx: -0.5, '&:hover': { bgcolor: 'var(--rt-hover)' } }}>
      <UserAvatar name={person.username} size={30} />
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 13.5, color: INK, fontWeight: 600 }}>
          {person.username}
        </Typography>
        <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>
          {person.email}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

/**
 * Pannello laterale di un utente: dettaglio (ruoli, people manager, riporti, progetti e team, dati di sistema)
 * e modifica/creazione nello stesso pannello. creating = nuovo utente.
 */
export default function UserPanel({ open, user, creating, users, roles, peopleRoles, onClose, onOpenUser, onSaved }) {
  const userId = user?.id;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [assignments, setAssignments] = useState({ key: null, rows: null, error: null });
  const [showAllReports, setShowAllReports] = useState(false);

  useEffect(() => {
    setEditing(!!creating);
    setForm(!creating && user ? formFrom(user) : EMPTY_FORM);
    setErrors({});
    setShowAllReports(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, creating]);

  useEffect(() => {
    if (!open || creating || !userId) return undefined;
    let cancelled = false;
    setAssignments((a) => (a.key === userId ? a : { key: userId, rows: null, error: null }));
    fetchUserAssignments(userId)
      .then((rows) => !cancelled && setAssignments({ key: userId, rows, error: null }))
      .catch((e) => !cancelled && setAssignments({ key: userId, rows: [], error: e.message }));
    return () => {
      cancelled = true;
    };
  }, [open, creating, userId]);

  const byId = useMemo(() => new Map(users.map((u) => [String(u.id), u])), [users]);
  const reports = useMemo(
    () => (userId ? users.filter((u) => String(u.id_people_manager) === String(userId)).sort((a, b) => a.username.localeCompare(b.username)) : []),
    [users, userId],
  );
  // Come people manager non si può scegliere la persona stessa né un suo riporto (eviterebbe un ciclo)
  const managerOptions = useMemo(() => {
    const blocked = !creating && userId ? descendantIds(users, userId) : new Set();
    return users
      .filter((u) => creating || !userId || (String(u.id) !== String(userId) && !blocked.has(String(u.id))))
      .sort((a, b) => a.username.localeCompare(b.username));
  }, [users, userId, creating]);

  const manager = user ? byId.get(String(user.id_people_manager)) : null;
  const set = (field) => (value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined, general: undefined }));
  };

  const save = async () => {
    const e = validateUserForm(form, users, creating ? null : userId);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    const payload = {
      username: form.username.trim(),
      email: form.email.trim().toLowerCase(),
      role_id: form.role_id,
      org_role_id: form.org_role_id,
      id_people_manager: form.id_people_manager,
    };
    try {
      const res = creating ? await createUser(payload) : await updateUser(userId, payload);
      setEditing(false);
      onSaved(creating ? res.user?.id : userId, creating ? 'User created' : 'User updated');
    } catch (err) {
      setErrors({ general: `Not saved: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };
  const cancel = () => {
    if (creating) {
      onClose();
      return;
    }
    setEditing(false);
    setForm(formFrom(user));
    setErrors({});
  };

  // Un nuovo utente parte sempre dal form (anche al primo render, prima dell'effetto che imposta editing)
  const showForm = creating || editing;
  const title = creating ? 'New user' : user?.username;
  const shownReports = showAllReports ? reports : reports.slice(0, REPORTS_PREVIEW);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => !saving && onClose()}
      PaperProps={{ sx: sidePanelPaperSx }}
      ModalProps={{ BackdropProps: { sx: sidePanelBackdropSx } }}
    >
      {(user || creating) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Intestazione */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, pt: 2.5, pb: 2 }}>
            <UserAvatar name={creating ? '+' : user.username} size={46} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography noWrap sx={{ fontSize: 18, fontWeight: 800, color: INK }}>
                {title}
              </Typography>
              {!creating && (
                <Typography noWrap sx={{ fontSize: 13, color: MUTED }}>
                  {user.email}
                </Typography>
              )}
            </Box>
            {!creating && !editing && (
              <Button variant="outlined" color="inherit" size="small" startIcon={<EditIcon />} onClick={() => setEditing(true)} sx={{ textTransform: 'none', borderColor: 'var(--rt-pill-border)', fontWeight: 600 }}>
                Edit
              </Button>
            )}
            <Tooltip title="Close">
              <IconButton onClick={onClose} disabled={saving} size="small">
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, pb: 2 }}>
            {showForm ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1.5 }}>
                <TextField label="Username" size="small" value={form.username} onChange={(e) => set('username')(e.target.value)} error={!!errors.username} helperText={errors.username} autoFocus={creating} />
                <TextField label="Email" size="small" value={form.email} onChange={(e) => set('email')(e.target.value)} error={!!errors.email} helperText={errors.email} />
                <TextField select label="App role" size="small" value={form.role_id} onChange={(e) => set('role_id')(e.target.value)} error={!!errors.role_id} helperText={errors.role_id || 'What the user can see and do in RUNTime'}>
                  {roles.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {getRoleDisplayName(Number(r.id))}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField select label="Hierarchical role" size="small" value={form.org_role_id} onChange={(e) => set('org_role_id')(e.target.value)} error={!!errors.org_role_id} helperText={errors.org_role_id}>
                  {peopleRoles.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Autocomplete
                  size="small"
                  options={managerOptions}
                  value={managerOptions.find((u) => String(u.id) === String(form.id_people_manager)) || byId.get(String(form.id_people_manager)) || null}
                  onChange={(_, v) => set('id_people_manager')(v ? v.id : '')}
                  getOptionLabel={(u) => u.username || ''}
                  isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                  filterOptions={(opts, { inputValue }) => {
                    const q = inputValue.trim().toLowerCase();
                    const list = q ? opts.filter((u) => u.username.toLowerCase().includes(q) || String(u.email || '').toLowerCase().includes(q)) : opts;
                    return list.slice(0, 50);
                  }}
                  renderOption={(props, u) => (
                    <Box component="li" {...props} key={u.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <UserAvatar name={u.username} size={26} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: 13.5 }}>
                          {u.username}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>
                          {u.email}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="People manager"
                      placeholder="Search by name or email"
                      InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                      error={!!errors.id_people_manager}
                      helperText={errors.id_people_manager}
                    />
                  )}
                />
                {errors.general && <Alert severity="error">{errors.general}</Alert>}
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pb: 1.75 }}>
                  <RoleChip roleId={user.role_id} />
                  {user.org_role?.name && (
                    <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 999, border: '1px solid var(--rt-pill-border)', color: INK, fontSize: 12, fontWeight: 600 }}>
                      {user.org_role.name}
                    </Box>
                  )}
                  {reports.length > 0 && (
                    <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 999, bgcolor: GREEN_SOFT, color: 'var(--rt-green-dark)', fontSize: 12, fontWeight: 600 }}>
                      Manages {reports.length} {reports.length === 1 ? 'person' : 'people'}
                    </Box>
                  )}
                </Box>

                <PanelSection title="People manager">
                  {manager ? (
                    <PersonLink person={manager} onOpen={onOpenUser} />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'var(--rt-warn)' }}>
                      <WarningIcon sx={{ fontSize: 18 }} />
                      <Typography sx={{ fontSize: 13.5, color: 'inherit' }}>Not assigned</Typography>
                    </Box>
                  )}
                </PanelSection>

                {reports.length > 0 && (
                  <PanelSection title={`Direct reports · ${reports.length}`}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.5 }}>
                      {shownReports.map((r) => (
                        <PersonLink key={r.id} person={r} onOpen={onOpenUser} />
                      ))}
                    </Box>
                    {reports.length > REPORTS_PREVIEW && (
                      <Link component="button" type="button" underline="hover" onClick={() => setShowAllReports((v) => !v)} sx={{ mt: 1, fontSize: 13, fontWeight: 600, color: 'var(--rt-green-dark)' }}>
                        {showAllReports ? 'Show fewer' : `Show all ${reports.length}`}
                      </Link>
                    )}
                  </PanelSection>
                )}

                <PanelSection title={`Projects and teams${assignments.rows ? ` · ${assignments.rows.length}` : ''}`}>
                  {assignments.error && <Alert severity="error">{`Unable to load projects: ${assignments.error}`}</Alert>}
                  {!assignments.rows ? (
                    <>
                      <Skeleton height={28} />
                      <Skeleton height={28} width="80%" />
                    </>
                  ) : assignments.rows.length === 0 ? (
                    <Typography sx={{ fontSize: 13.5, color: MUTED }}>Not assigned to any project.</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {assignments.rows.map((a) => (
                        <Box key={a.projectId}>
                          <Typography sx={{ fontSize: 13.5, color: INK, fontWeight: 600 }}>
                            {a.project}
                            {a.customer && (
                              <Typography component="span" sx={{ fontSize: 12.5, color: MUTED, fontWeight: 400 }}>
                                {' '}
                                · {a.customer}
                              </Typography>
                            )}
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                            {a.roles.length ? a.roles.join(', ') : 'No project role'} · {a.teams.length ? a.teams.join(', ') : 'no team'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </PanelSection>

                <PanelSection title="System">
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', rowGap: 1, columnGap: 2 }}>
                    {[
                      ['User ID', user.id],
                      ['Level / company', `${user.level_id ?? '—'} / ${user.company_id ?? '—'}`],
                      ['Created', fmtDate(user.created_at)],
                      ['Last updated', fmtDate(user.updated_at)],
                    ].map(([label, value]) => (
                      <Box key={label}>
                        <Typography sx={{ fontSize: 12, color: MUTED }}>{label}</Typography>
                        <Typography sx={{ fontSize: 13.5, color: INK }}>{value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </PanelSection>
              </>
            )}
          </Box>

          {showForm && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 2.5, py: 1.5, borderTop: `1px solid ${LINE}` }}>
              <Button color="inherit" onClick={cancel} disabled={saving} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button variant="contained" color="success" onClick={save} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none', minWidth: 120, bgcolor: GREEN }}>
                {saving ? <CircularProgress size={18} color="inherit" /> : creating ? 'Create user' : 'Save changes'}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  );
}
